import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import componentRegistry from '../injector';
import MyAccountNavigation from './MyAccountNavigation';
import MyAccountCard from './MyAccountCard';
import OrderActions from './OrderActions';
import ViewOrder from './ViewOrder';
import { MenuItem } from '../types/myaccount';
import MyAccountPageLayout from './MyAccountPageLayout';
import { LayoutPage, PageLayouts } from './myAccountLayoutWidgets';

interface MyAccountProps {
    menuItems?: MenuItem[];
    userName?: string;
    basePath?: string;
    pageLayouts?: PageLayouts;
    enabledPackages?: string[];
    noticesHtml?: string;
    featureFlags?: {
        orderActionsEnabled?: boolean;
        accountSummaryEnabled?: boolean;
        accountSummaryConfig?: {
            widgetDefaults?: Record<string, any>;
        };
        orderHistoryEnabled?: boolean;
        invoiceStatementEnabled?: boolean;
        pricingCsvExportEnabled?: boolean;
        pricingCsvExportUrl?: string;
        myAccountCommerceEnabled?: boolean;
        myAccountCommerceFlow?: 'standard' | 'express';
    };
}

interface MainContentErrorBoundaryProps {
    routePath: string;
    children: React.ReactNode;
}

interface MainContentErrorBoundaryState {
    hasError: boolean;
    errorMessage: string;
}

class MainContentErrorBoundary extends React.Component<MainContentErrorBoundaryProps, MainContentErrorBoundaryState> {
    constructor(props: MainContentErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error: Error) {
        return {
            hasError: true,
            errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        };
    }

    componentDidCatch(error: Error) {
        console.error('My Account route render error:', error);
    }

    componentDidUpdate(prevProps: MainContentErrorBoundaryProps) {
        // Reset the boundary on route change so navigation still works after a crash.
        if (prevProps.routePath !== this.props.routePath && this.state.hasError) {
            this.setState({ hasError: false, errorMessage: '' });
        }
    }

    private getSectionLabel(): string {
        return 'this page';
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="woocommerce-MyAccount-content">
                    <h2>{this.getSectionLabel()}</h2>
                    <div className="woocommerce-error" role="alert">
                        Failed to load {this.getSectionLabel()}. Please try again.
                    </div>
                    {this.state.errorMessage && (
                        <p className="text-muted" style={{ marginTop: '8px' }}>
                            {this.state.errorMessage}
                        </p>
                    )}
                    <button
                        type="button"
                        className="button"
                        onClick={() => this.setState({ hasError: false, errorMessage: '' })}
                    >
                        Retry
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

const MyAccountContent: React.FC<MyAccountProps> = ({
    menuItems,
    userName = 'User',
    pageLayouts = {},
    enabledPackages = [],
    noticesHtml = '',
    featureFlags
}) => {
    const location = useLocation();
    const [globalNoticesHtml, setGlobalNoticesHtml] = useState<string>(String(noticesHtml || '').trim());
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const previousPathnameRef = useRef<string>(location.pathname);

    const dynamicRoutes = Array.isArray(menuItems)
        ? menuItems.reduce<{ path: string; component: string; menuItemId: string; routeProps?: Record<string, any> }[]>((routes, item) => {
            if (!Array.isArray(item.reactRoutes)) {
                return routes;
            }

            for (const route of item.reactRoutes) {
                if (!route || typeof route.path !== 'string' || typeof route.component !== 'string') {
                    continue;
                }

                const path = route.path.trim();
                const component = route.component.trim();
                if (!path || !component) {
                    continue;
                }

                if (!routes.some((existing) => existing.path === path && existing.component === component)) {
                    routes.push({
                        path,
                        component,
                        menuItemId: item.id,
                        routeProps: {
                            ...(route.props && typeof route.props === 'object' ? route.props : {}),
                            wekaPagePermissions: item.pagePermissionValues && typeof item.pagePermissionValues === 'object'
                                ? item.pagePermissionValues
                                : {},
                            wekaPagePermissionDefinitions: Array.isArray(item.permissions) ? item.permissions : [],
                            wekaPagePackage: typeof item.package === 'string' ? item.package : '',
                        },
                    });
                }
            }

            return routes;
        }, [])
        : [];

    const layoutPages: LayoutPage[] = Array.isArray(menuItems)
        ? menuItems
            .filter((item) => Array.isArray(item.reactRoutes) && item.reactRoutes.length > 0)
            .map((item) => ({
                id: item.id,
                label: item.label,
                routes: item.reactRoutes!.map((route) => ({
                    path: route.path,
                    component: route.component,
                    props: route.props,
                })),
            }))
        : [];

    // Remove loading skeleton when React mounts
    useEffect(() => {
        const skeleton = document.querySelector('.my-account-loading-skeleton');
        if (skeleton) {
            skeleton.remove();
        }
    }, []);

    useEffect(() => {
        if (previousPathnameRef.current !== location.pathname && globalNoticesHtml !== '') {
            setGlobalNoticesHtml('');
        }

        previousPathnameRef.current = location.pathname;
    }, [globalNoticesHtml, location.pathname]);

    useEffect(() => {
        setIsMobileNavOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isMobileNavOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMobileNavOpen(false);
            }
        };

        window.addEventListener('keydown', handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleEscape);
        };
    }, [isMobileNavOpen]);

    const orderActionsEnabled = featureFlags?.orderActionsEnabled === true;
    return (
        <div className="weka-my-account-react-app container-fluid px-0 mt-4 mb-4">
            <div className={`weka-my-account-mobile-nav-overlay${isMobileNavOpen ? ' is-open' : ''}`} aria-hidden={!isMobileNavOpen}>
                <button
                    type="button"
                    className="weka-my-account-mobile-nav-overlay__backdrop"
                    aria-label="Close account menu"
                    onClick={() => setIsMobileNavOpen(false)}
                ></button>
                <div id="weka-my-account-mobile-nav" className="weka-my-account-mobile-nav-overlay__sheet" role="dialog" aria-modal="true" aria-label="Account menu">
                    <div className="weka-my-account-mobile-nav-overlay__header">
                        <span className="weka-my-account-mobile-nav-overlay__title">Account Menu</span>
                        <button
                            type="button"
                            className="weka-my-account-mobile-nav-overlay__close"
                            aria-label="Close account menu"
                            onClick={() => setIsMobileNavOpen(false)}
                        >
                            <i className="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                    <MyAccountNavigation
                        menuItems={menuItems}
                        wrapInSidebar={false}
                        className="weka-my-account-mobile-nav"
                        onNavigate={() => setIsMobileNavOpen(false)}
                    />
                </div>
            </div>
            <div className="row g-3 g-lg-4 my-account-content-row">
                {/* Sidebar Navigation */}
                <aside className="col-12 col-lg-auto my-account-sidebar">
                    <button
                        type="button"
                        className="weka-my-account-mobile-nav-trigger d-lg-none"
                        onClick={() => setIsMobileNavOpen(true)}
                        aria-expanded={isMobileNavOpen}
                        aria-controls="weka-my-account-mobile-nav"
                    >
                        <i className="fas fa-bars" aria-hidden="true"></i>
                        <span>Menu</span>
                    </button>
                    <div className="d-none d-lg-block">
                        <MyAccountNavigation menuItems={menuItems} />
                    </div>
                    {orderActionsEnabled && (
                        <MyAccountCard
                            variant="page"
                            slug="order-actions"
                            className="p-3"
                            wrapContent={false}
                        >
                            <OrderActions />
                        </MyAccountCard>
                    )}
                </aside>

                {/* Main Content Area */}
                <main className="col-12 col-lg my-account-main-content">
                    {globalNoticesHtml !== '' && (
                        <div
                            className="woocommerce-notices-wrapper weka-my-account-notices mb-3"
                            dangerouslySetInnerHTML={{ __html: globalNoticesHtml }}
                        />
                    )}
                    <MainContentErrorBoundary routePath={location.pathname}>
                        <Routes>
                            {dynamicRoutes.map((route) => {
                                if (!componentRegistry[route.component]) {
                                    return null;
                                }

                                return (
                                    <Route
                                        key={`${route.path}:${route.component}`}
                                        path={route.path}
                                        element={
                                            <MyAccountPageLayout
                                                pageId={route.menuItemId}
                                                pages={layoutPages}
                                                layout={pageLayouts?.[route.menuItemId]}
                                                routeComponent={route.component}
                                                routeProps={route.routeProps}
                                                userName={userName}
                                                featureFlags={featureFlags}
                                                enabledPackages={enabledPackages}
                                            />
                                        }
                                    />
                                );
                            })}

                            {/* View Order Detail */}
                            <Route
                                path="/view-order/:orderId"
                                element={<ViewOrder featureFlags={featureFlags} displayMode="order-details" />}
                            />

                            <Route
                                path="/order-received/:orderId"
                                element={<ViewOrder featureFlags={featureFlags} displayMode="order-received" />}
                            />

                            {/* Catch all - redirect to dashboard */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </MainContentErrorBoundary>
                </main>
            </div>
        </div>
    );
};

const MyAccount: React.FC<MyAccountProps> = ({
    menuItems,
    userName = 'User',
    basePath = '/my-account',
    pageLayouts = {},
    enabledPackages = [],
    noticesHtml = '',
    featureFlags
}) => {
    // Get the initial path from window.location
    const [initialPath, setInitialPath] = useState<string | null>(null);

    useEffect(() => {
        // Extract the path after the basePath
        const fullPath = window.location.pathname;
        if (fullPath.startsWith(basePath)) {
            const path = fullPath.substring(basePath.length);
            setInitialPath(path || '/');
        }
    }, [basePath]);

    // Don't render until we've determined the initial path
    if (initialPath === null) {
        return null;
    }

    return (
        <Router basename={basePath}>
            <MyAccountContent
                menuItems={menuItems}
                userName={userName}
                pageLayouts={pageLayouts}
                enabledPackages={enabledPackages}
                noticesHtml={noticesHtml}
                featureFlags={featureFlags}
            />
        </Router>
    );
};

export default MyAccount;
